<template>
  <v-app>
    <v-navigation-drawer
      v-model="drawer"
      app
      :permanent="$vuetify.display.lgAndUp"
      :temporary="$vuetify.display.mdAndDown"
    >
      <v-list>
        <v-list-item
          prepend-avatar="https://randomuser.me/api/portraits/men/85.jpg"
          :title="currentUser?.full_name"
          :subtitle="currentUser?.role"
        ></v-list-item>

        <v-divider></v-divider>

        <v-list-item
          v-for="item in menuItems"
          :key="item.title"
          :to="item.path"
          :prepend-icon="item.icon"
          :title="item.title"
          :disabled="!hasAccess(item.requiredRole)"
        ></v-list-item>
      </v-list>
    </v-navigation-drawer>

    <v-app-bar app color="primary">
      <v-app-bar-nav-icon
        v-if="$vuetify.display.mdAndDown"
        @click="drawer = !drawer"
      ></v-app-bar-nav-icon>

      <v-toolbar-title class="text-white">
        Hệ thống Quản lý Chấm Công
      </v-toolbar-title>

      <v-spacer></v-spacer>

      <v-btn icon @click="toggleTheme">
        <v-icon>{{ isDarkTheme ? 'mdi-white-balance-sunny' : 'mdi-moon-waning-crescent' }}</v-icon>
      </v-btn>

      <v-menu>
        <template v-slot:activator="{ props }">
          <v-btn icon v-bind="props">
            <v-icon>mdi-account-circle</v-icon>
          </v-btn>
        </template>

        <v-list>
          <v-list-item @click="changePassword">
            <v-list-item-title>Đổi mật khẩu</v-list-item-title>
          </v-list-item>
          <v-list-item @click="logout">
            <v-list-item-title>Đăng xuất</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </v-app-bar>

    <v-main>
      <v-container fluid>
        <router-view></router-view>
      </v-container>
    </v-main>

    <!-- Dialog đổi mật khẩu -->
    <v-dialog v-model="changePasswordDialog" max-width="500">
      <v-card>
        <v-card-title>Đổi mật khẩu</v-card-title>
        <v-card-text>
          <v-form ref="changePasswordForm" v-model="isChangePasswordValid">
            <v-text-field
              v-model="passwordForm.currentPassword"
              label="Mật khẩu hiện tại"
              type="password"
              :rules="[v => !!v || 'Vui lòng nhập mật khẩu hiện tại']"
              required
            ></v-text-field>

            <v-text-field
              v-model="passwordForm.newPassword"
              label="Mật khẩu mới"
              type="password"
              :rules="[
                v => !!v || 'Vui lòng nhập mật khẩu mới',
                v => v.length >= 8 || 'Mật khẩu phải có ít nhất 8 ký tự'
              ]"
              required
            ></v-text-field>

            <v-text-field
              v-model="passwordForm.confirmPassword"
              label="Xác nhận mật khẩu mới"
              type="password"
              :rules="[
                v => !!v || 'Vui lòng xác nhận mật khẩu mới',
                v => v === passwordForm.newPassword || 'Mật khẩu xác nhận không khớp'
              ]"
              required
            ></v-text-field>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" @click="changePasswordDialog = false">Hủy</v-btn>
          <v-btn
            color="primary"
            :loading="isChangingPassword"
            :disabled="!isChangePasswordValid"
            @click="submitChangePassword"
          >
            Xác nhận
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-app>
</template>

<script>
import { ref, computed } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import { useTheme } from 'vuetify'

export default {
  name: 'DefaultLayout',
  
  setup() {
    const store = useStore()
    const router = useRouter()
    const theme = useTheme()
    
    const drawer = ref(true)
    const changePasswordDialog = ref(false)
    const isChangePasswordValid = ref(false)
    const isChangingPassword = ref(false)
    const changePasswordForm = ref(null)
    
    const passwordForm = ref({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    })

    const currentUser = computed(() => store.getters['auth/currentUser'])
    const isDarkTheme = computed(() => theme.global.current.value.dark)

    const menuItems = [
      {
        title: 'Dashboard',
        icon: 'mdi-view-dashboard',
        path: '/dashboard',
        requiredRole: 'hr'
      },
      {
        title: 'Quản lý Ca kíp',
        icon: 'mdi-calendar-clock',
        path: '/shifts',
        requiredRole: 'hr'
      },
      {
        title: 'Quản lý Nhân sự',
        icon: 'mdi-account-group',
        path: '/personnel',
        requiredRole: 'hr'
      },
      {
        title: 'Dữ liệu Chấm công',
        icon: 'mdi-fingerprint',
        path: '/attendance',
        requiredRole: 'hr'
      },
      {
        title: 'Điểm chấm công',
        icon: 'mdi-map-marker',
        path: '/attendance/locations',
        requiredRole: 'admin'
      },
      {
        title: 'Đồng bộ HRM',
        icon: 'mdi-sync',
        path: '/hrm-sync',
        requiredRole: 'admin'
      },
      {
        title: 'Cài đặt',
        icon: 'mdi-cog',
        path: '/settings',
        requiredRole: 'admin'
      }
    ]

    const hasAccess = (requiredRole) => {
      if (!requiredRole) return true
      return store.getters['auth/hasAccess'](requiredRole)
    }

    const toggleTheme = () => {
      theme.global.name.value = isDarkTheme.value ? 'light' : 'dark'
    }

    const changePassword = () => {
      changePasswordDialog.value = true
    }

    const submitChangePassword = async () => {
      if (!changePasswordForm.value.validate()) return

      isChangingPassword.value = true
      try {
        await store.dispatch('auth/changePassword', {
          currentPassword: passwordForm.value.currentPassword,
          newPassword: passwordForm.value.newPassword
        })
        
        changePasswordDialog.value = false
        passwordForm.value = {
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        }
      } catch (error) {
        console.error('Lỗi khi đổi mật khẩu:', error)
      } finally {
        isChangingPassword.value = false
      }
    }

    const logout = async () => {
      await store.dispatch('auth/logout')
      router.push('/login')
    }

    return {
      drawer,
      menuItems,
      currentUser,
      isDarkTheme,
      changePasswordDialog,
      isChangePasswordValid,
      isChangingPassword,
      passwordForm,
      changePasswordForm,
      hasAccess,
      toggleTheme,
      changePassword,
      submitChangePassword,
      logout
    }
  }
}
</script>